import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
  Stack
} from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Link } from "react-router-dom";
const PunchLogsDashboard = ({ apiResponse }) => {
  // Safe check if logs exist
  const logs = apiResponse?.data || [];

  return (
    <Card
      sx={{
        width: '100%',
        mx: 'auto',
        mt: 3,
        boxShadow: '0px 4px 20px rgba(0,0,0,0.08)',
        borderRadius: 3
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
        {/* Header Section */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h6" fontWeight="bold" color="text.primary" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Live Punch Logs
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              Real-time employee check-in/out tracking
            </Typography>
          </Box>
          <Chip
            label={`Total Records: ${logs.length}`}
            color="secondary"
            size="small"
            variant="filled"
            sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5', color: '#616161', alignSelf: { xs: 'flex-start', sm: 'center' } }}
          />
        </Stack>

        {/* Mobile view: stacked cards (xs only) */}
        <Box sx={{ display: { xs: 'block', sm: 'block', lg: 'none' } }}>
          {logs.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body2">No punch data available for today.</Typography>
            </Box>
          ) : (
            <Stack
              spacing={1.25}
              sx={{
                maxHeight: 440,
                overflowY: 'auto',
                pr: 0.5,
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-track': { background: '#f1f1f1' },
                '&::-webkit-scrollbar-thumb': { background: '#ccc', borderRadius: '4px' },
              }}
            >
              {logs.map((log) => (
                <Paper
                  key={log.id}
                  variant="outlined"
                  sx={{ p: 1.5, borderRadius: 2, borderColor: '#e0e0e0' }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography
                      component={Link}
                      to="/employeepro/attendance"
                      state={{ employeeName: log.employee_name }}
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        textTransform: "capitalize",
                        color: "#1976d2",
                        textDecoration: "none",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      {log.employee_name}
                    </Typography>
                    <Chip
                      icon={log.type === 'IN' ? <LoginIcon fontSize="small" /> : <LogoutIcon fontSize="small" />}
                      label={log.type}
                      size="small"
                      sx={{
                        fontWeight: 'bold',
                        width: 75,
                        justifyContent: 'center',
                        flexShrink: 0,
                        backgroundColor: log.type === 'IN' ? '#e8f5e9' : '#ffebee',
                        color: log.type === 'IN' ? '#2e7d32' : '#c62828',
                      }}
                    />
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {log.site_name}
                  </Typography>

                  {log.place ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, opacity: 0.8 }}>
                      <LocationOnIcon sx={{ fontSize: 14, color: 'text.secondary', mr: 0.5, flexShrink: 0 }} />
                      <Typography variant="caption" color="text.secondary">
                        {log.place}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                      No GPS location captured
                    </Typography>
                  )}

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {log.punch_time}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          )}
        </Box>

        {/* Desktop / tablet view: table (sm and up) */}
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            display: { xs: 'none', sm: 'none', lg: 'block' },
            maxHeight: 440,
            width: '100%',
            overflowX: 'auto',
            overflowY: 'auto',
            borderRadius: 2,
            borderColor: '#e0e0e0',
            '&::-webkit-scrollbar': { width: '6px', height: '6px' },
            '&::-webkit-scrollbar-track': { background: '#f1f1f1' },
            '&::-webkit-scrollbar-thumb': { background: '#ccc', borderRadius: '4px' },
            '&::-webkit-scrollbar-thumb:hover': { background: '#aaa' },
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8f9fa', whiteSpace: 'nowrap' }}>Employee</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8f9fa', whiteSpace: 'nowrap' }} align="center">Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8f9fa', whiteSpace: 'nowrap' }}>Site / Location</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8f9fa', whiteSpace: 'nowrap' }}>Punch Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No punch data available for today.
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow
                    key={log.id}
                    hover
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    {/* Employee Name */}
                    <TableCell sx={{ fontWeight: 500, whiteSpace: "nowrap" }}>
                      <Link
                        to="/employeepro/attendance"
                        state={{ employeeName: log.employee_name }}
                        style={{
                          color: "#1976d2",
                          textDecoration: "none",
                          fontWeight: 600,
                        }}
                        onMouseEnter={(e) => (e.target.style.textDecoration = "underline")}
                        onMouseLeave={(e) => (e.target.style.textDecoration = "none")}
                      >
                        {log.employee_name}
                      </Link>
                    </TableCell>

                    {/* Punch Type Status Badge */}
                    <TableCell align="center">
                      <Chip
                        icon={log.type === 'IN' ? <LoginIcon fontSize="small" /> : <LogoutIcon fontSize="small" />}
                        label={log.type}
                        size="small"
                        sx={{
                          fontWeight: 'bold',
                          width: 75,
                          justifyContent: 'center',
                          backgroundColor: log.type === 'IN' ? '#e8f5e9' : '#ffebee',
                          color: log.type === 'IN' ? '#2e7d32' : '#c62828',
                        }}
                      />
                    </TableCell>

                    {/* Site & Address with Tooltip for long texts */}
                    <TableCell sx={{ maxWidth: 250 }}>
                      <Typography variant="body2" fontWeight="500" noWrap>
                        {log.site_name}
                      </Typography>
                      {log.place ? (
                        <Tooltip title={log.place} arrow placement="top">
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, opacity: 0.8 }}>
                            <LocationOnIcon sx={{ fontSize: 14, color: 'text.secondary', mr: 0.5, flexShrink: 0 }} />
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 220 }}>
                              {log.place}
                            </Typography>
                          </Box>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                          No GPS location captured
                        </Typography>
                      )}
                    </TableCell>

                    {/* Punch Time */}
                    <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: '13px' }}>
                      {log.punch_time}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default PunchLogsDashboard;